import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, BRAND } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, ChevronLeft, Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function DeleteAccount() {
  const { workshop, logout } = useAuth();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  const canDelete = confirm.trim().toUpperCase() === "ELIMINAR";

  const doDelete = async () => {
    if (!canDelete) return;
    if (!window.confirm("Esta acción es DEFINITIVA. ¿Confirmas que quieres eliminar tu cuenta y todos los datos del taller?")) return;
    setDeleting(true);
    try {
      await api.delete("/auth/me");
      toast.success("Cuenta y todos los datos eliminados.");
      await logout();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(errMsg(e, "No se pudo eliminar la cuenta"));
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200" data-testid="delete-account-page">
      <header className="sticky top-0 z-30 bg-slate-950/80 backdrop-blur border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo size={32} />
            <span className="font-display font-bold text-white">{BRAND.name}</span>
          </Link>
          <Link to="/" className="text-sm text-slate-300 hover:text-white inline-flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" /> Volver
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10 space-y-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2">Eliminar mi cuenta</h1>
          <p className="text-slate-400">
            Aquí puedes eliminar de forma permanente tu cuenta de {BRAND.name} y todos los datos asociados a tu taller.
            Este es un derecho reconocido por el artículo 17 del RGPD ("derecho al olvido").
          </p>
        </div>

        <section className="rounded-2xl bg-slate-900/60 border border-white/10 p-6 space-y-4">
          <h2 className="font-display text-xl font-bold text-white">Qué se elimina</h2>
          <ul className="list-disc pl-6 space-y-1 text-sm text-slate-300">
            <li>Datos de tu cuenta (email, contraseña, nombre del taller y responsable).</li>
            <li>Todos los mecánicos, sus horarios y vacaciones.</li>
            <li>Todas las reparaciones (agenda general y mecánica rápida).</li>
            <li>El historial completo de vehículos y matrículas guardadas.</li>
            <li>Los días festivos personalizados de tu taller.</li>
            <li>Tus preferencias y ajustes (ratio 60/40, días laborables).</li>
            <li>Las sesiones activas se cierran inmediatamente.</li>
          </ul>
          <h2 className="font-display text-xl font-bold text-white mt-4">Qué se conserva</h2>
          <ul className="list-disc pl-6 space-y-1 text-sm text-slate-300">
            <li>Registros de facturación en Stripe conforme a nuestras obligaciones contables (6 años, artículo 30 del Código de Comercio). Estos registros están anonimizados en la medida legalmente posible.</li>
          </ul>
          <p className="text-xs text-slate-500">Si tenías una suscripción activa, la cancelaremos automáticamente en Stripe.</p>
        </section>

        <section className="rounded-2xl bg-red-950/40 border border-red-500/40 p-6 space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-400 shrink-0 mt-0.5" />
            <div>
              <h2 className="font-display text-xl font-bold text-red-100">Zona peligrosa</h2>
              <p className="text-sm text-red-200 mt-1">Esta acción es <strong>inmediata e irreversible</strong>. No hay papelera de reciclaje. Si estás seguro, escribe la palabra <strong>ELIMINAR</strong> abajo.</p>
            </div>
          </div>

          {workshop ? (
            <>
              <p className="text-sm text-slate-300">
                Cuenta activa: <strong className="text-white">{workshop.name}</strong> ({workshop.email || workshop.code}).
              </p>
              <Input
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder='Escribe "ELIMINAR" para confirmar'
                className="h-11 bg-slate-950 border-red-500/40 text-white"
                data-testid="confirm-delete-input"
              />
              <Button
                onClick={doDelete}
                disabled={!canDelete || deleting}
                className="h-11 bg-red-600 hover:bg-red-700 disabled:bg-red-900 disabled:opacity-60"
                data-testid="confirm-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {deleting ? "Eliminando..." : "Eliminar mi cuenta definitivamente"}
              </Button>
            </>
          ) : (
            <div className="rounded-lg bg-slate-900/60 border border-white/10 p-4 space-y-3">
              <p className="text-sm text-slate-300">
                No has iniciado sesión. Puedes eliminar tu cuenta de dos formas:
              </p>
              <ol className="list-decimal pl-6 space-y-1 text-sm text-slate-300">
                <li>
                  <Link to="/entrar" className="text-blue-400 underline">Inicia sesión</Link> y vuelve a esta página para pulsar el botón de eliminar.
                </li>
                <li>
                  Envía un correo a{" "}
                  <a href={`mailto:${BRAND.contactEmail}?subject=Eliminación%20de%20cuenta%20-%20RGPD`} className="text-blue-400 underline">
                    {BRAND.contactEmail}
                  </a>{" "}
                  desde el correo con el que te registraste, indicando "Eliminación de cuenta". Responderemos y borraremos tus datos en un plazo máximo de 30 días.
                </li>
              </ol>
              <a
                href={`mailto:${BRAND.contactEmail}?subject=Eliminaci%C3%B3n%20de%20cuenta%20-%20RGPD&body=Hola%2C%0A%0ASolicito%20la%20eliminaci%C3%B3n%20de%20mi%20cuenta%20y%20de%20todos%20los%20datos%20asociados%20a%20mi%20taller%20en%20Citas%20Taller.%0A%0AEmail%20de%20la%20cuenta%3A%20%5Btu%20email%5D%0ANombre%20del%20taller%3A%20%5Btu%20taller%5D%0A%0AGracias.`}
                className="inline-flex items-center gap-2 h-11 px-4 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium"
                data-testid="delete-account-email-btn"
              >
                <Mail className="w-4 h-4" /> Solicitar eliminación por email
              </a>
            </div>
          )}
        </section>

        <p className="text-xs text-slate-500">
          Base legal: artículo 17 del Reglamento (UE) 2016/679 (RGPD) y artículo 15 de la Ley Orgánica 3/2018 (LOPDGDD).
          Más información en nuestra{" "}
          <Link to="/privacidad" className="text-blue-400 underline">Política de Privacidad</Link>.
        </p>
      </main>
    </div>
  );
}
